// src/services/SocketSyncService.js
/**
 * Este servicio centraliza la conexión socket.io y sincroniza datos entre ventanas
 * usando BroadcastChannel API.
 */
import io from 'socket.io-client';

class SocketSyncService {
  constructor() {
    this.socket = null;
    this.channel = null;
    this.isMainConnection = false;
    this.mainWindowId = null;
    this.myWindowId = `window_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.pingInterval = null;
    this.listeners = new Map();
    this.lastEventData = null;
    this.lastClockData = null;
    this.lastResultsData = null;
    
    // Intentar inicializar inmediatamente
    this.init();
  }

  getConnectionStatus() {
    return {
      isConnected: this.socket?.connected || false,
      isMainConnection: this.isMainConnection,
      windowId: this.myWindowId,
      mainWindowId: this.mainWindowId
    };
  }
  
  // Añadir este método para permitir la reconexión manual
  reconnect() {
    if (this.socket) {
      console.log('Intentando reconectar socket...');
      this.socket.connect();
      return true;
    } else if (this.isMainConnection) {
      console.log('Iniciando nueva conexión...');
      this.setupSocketConnection();
      return true;
    } else {
      console.log('Esta ventana no es la principal, no puede reconectar');
      // Solicitar a la ventana principal que se reconecte
      this.channel.postMessage({
        type: 'request_reconnect',
        senderId: this.myWindowId
      });
      return false;
    }
  }
  

  init() {
    try {
      // Crear canal de comunicación entre ventanas
      this.channel = new BroadcastChannel('scoreboard_sync');
      
      // Configurar manejo de mensajes del canal
      this.channel.onmessage = (event) => {
        const { type, data, senderId } = event.data;
        
        // Ignorar mensajes propios
        if (senderId === this.myWindowId) return;
        
        switch (type) {
          case 'ping':
            // Responder al ping con un pong
            this.channel.postMessage({
              type: 'pong',
              senderId: this.myWindowId,
              timestamp: Date.now()
            });
            break;
            
          case 'pong':
            // No necesitamos hacer nada con los pongs aquí
            break;
            
          case 'main_window_announcement':
            this.mainWindowId = senderId;
            console.log(`Reconociendo ventana principal: ${this.mainWindowId}`);
            break;
            
          case 'socket_event':
            // Procesar evento de socket recibido desde la ventana principal
            this.handleSocketEvent(data);
            break;
            
          case 'request_latest_data':
            // Si somos la ventana principal, enviamos los datos más recientes
            if (this.isMainConnection && this.lastEventData) {
              this.channel.postMessage({
                type: 'socket_event',
                senderId: this.myWindowId,
                data: {
                  event: 'message',
                  message: this.lastEventData
                }
              });
            }
            break;
            case 'request_reconnect':
              // Si somos la ventana principal, intentar reconectar
              if (this.isMainConnection && this.socket) {
                console.log('Recibida solicitud de reconexión de otra ventana');
                this.socket.connect();
              }
              break;
            
          default:
            console.log('Mensaje desconocido recibido:', event.data);
        }
      };
      
      // Decidir si esta ventana será la principal o no
      this.determineMainWindow();
      
      // Solicitar datos más recientes a la ventana principal (si existe)
      setTimeout(() => {
        if (!this.isMainConnection) {
          this.channel.postMessage({
            type: 'request_latest_data',
            senderId: this.myWindowId
          });
        }
      }, 500);
      
      // Manejar el cierre de la ventana
      window.addEventListener('beforeunload', this.cleanup.bind(this));
      
      console.log('SocketSyncService inicializado correctamente');
    } catch (error) {
      console.error('Error inicializando SocketSyncService:', error);
      
      // Fallback: crear conexión socket directa si BroadcastChannel no es soportado
      if (!this.channel) {
        this.setupDirectSocketConnection();
      }
    }
  }

  determineMainWindow() {
    // Iniciar proceso de elección de ventana principal
    setTimeout(() => {
      // Enviar ping para descubrir otras ventanas
      this.channel.postMessage({
        type: 'ping',
        senderId: this.myWindowId,
        timestamp: Date.now()
      });
      
      // Esperar respuestas durante un breve período
      setTimeout(() => {
        // Si no se ha establecido una ventana principal, nos convertimos en la principal
        if (!this.mainWindowId) {
          this.isMainConnection = true;
          this.mainWindowId = this.myWindowId;
          console.log(`Esta ventana se convierte en la principal: ${this.myWindowId}`);
          
          // Anunciar a todas las ventanas que somos la principal
          this.channel.postMessage({
            type: 'main_window_announcement',
            senderId: this.myWindowId
          });
          
          // Establecer la conexión socket.io
          this.setupSocketConnection();
          
          // Configurar ping periódico para verificar si sigue activa
          this.pingInterval = setInterval(() => {
            this.channel.postMessage({
              type: 'ping',
              senderId: this.myWindowId,
              timestamp: Date.now()
            });
          }, 5000);
        }
      }, 300);
    }, 100);
  }

  setupSocketConnection() {
    if (this.socket) return;
    
    // Crear conexión socket.io
    this.socket = io.connect('http://localhost:8000');
    
    this.socket.on('connect', () => {
      console.log('Ventana principal conectada al servidor socket.io');
          // Difundir a todas las ventanas
    this.channel.postMessage({
      type: 'socket_event',
      senderId: this.myWindowId,
      data: {
        event: 'connect'
      }
    });
    
    // Notificar localmente
    this.notifyListeners('connect');
    });

    
    this.socket.on('disconnect', () => {
      console.log('Ventana principal desconectada del servidor socket.io');
          // Difundir a todas las ventanas
    this.channel.postMessage({
      type: 'socket_event',
      senderId: this.myWindowId,
      data: {
        event: 'disconnect'
      }
    });
    
    // Notificar localmente
    this.notifyListeners('disconnect');
    });
    
    // Escuchar todos los eventos relevantes
    this.socket.on('message', (message) => {
      // Guardar el último mensaje recibido
      this.lastEventData = message;
      
      // Difundir a todas las ventanas
      this.channel.postMessage({
        type: 'socket_event',
        senderId: this.myWindowId,
        data: {
          event: 'message',
          message
        }
      });
      
      // Procesar localmente también
      this.handleSocketEvent({
        event: 'message',
        message
      });
    });
    
    this.socket.on('clearBoard', () => {
      // Difundir a todas las ventanas
      this.channel.postMessage({
        type: 'socket_event',
        senderId: this.myWindowId,
        data: {
          event: 'clearBoard'
        }
      });
      
      // Procesar localmente también
      this.handleSocketEvent({
        event: 'clearBoard'
      });
    });
  }

  setupDirectSocketConnection() {
    // Método de respaldo si BroadcastChannel no es soportado
    console.log('Usando conexión socket directa (fallback)');
    this.socket = io.connect('http://localhost:8000');
    
    this.socket.on('connect', () => {
      console.log('Conectado al servidor socket.io (conexión directa)');
    });
    
    this.socket.on('disconnect', () => {
      console.log('Desconectado del servidor socket.io (conexión directa)');
    });
    
    this.socket.on('message', (message) => {
      this.notifyListeners('message', message);
    });
    
    this.socket.on('clearBoard', () => {
      this.notifyListeners('clearBoard');
    });
  }

  handleSocketEvent(data) {
    const { event, message } = data;

    if (event === 'connect' || event === 'disconnect') {
      // Notificar a todos los oyentes registrados sobre el evento de conexión
      this.notifyListeners(event);
      return;
    }
    
    // Analizar el mensaje si es necesario
    if (event === 'message' && message) {
      try {
        // Extraer reloj y resultados para caché
        const parsed = JSON.parse(message);
        if (parsed.length > 0 && parsed[0]) {
          this.lastClockData = parsed[0][0];
        }
        if (parsed.length > 2) {
          this.lastResultsData = parsed.slice(2);
        }
      } catch (error) {
        console.error('Error analizando mensaje:', error);
      }
    }
    
    // Notificar a todos los oyentes registrados
    this.notifyListeners(event, message);
  }

  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      for (const callback of this.listeners.get(event)) {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error en listener para evento ${event}:`, error);
        }
      }
    }
  }

  // Método para registrar oyentes
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Si tenemos datos en caché, enviarlos inmediatamente
    if (event === 'message' && this.lastEventData) {
      callback(this.lastEventData);
    }
    
    // Devolver función para eliminar el oyente
    return () => {
      const listeners = this.listeners.get(event);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  // Método para desregistrar oyentes
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  // Limpiar recursos al cerrar la ventana
  cleanup() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    if (this.channel) {
      // Si somos la ventana principal, informar para que otra pueda tomar el control
      if (this.isMainConnection) {
        this.channel.postMessage({
          type: 'main_window_closed',
          senderId: this.myWindowId
        });
      }
      
      this.channel.close();
    }
  }
}

// Exportar una única instancia para toda la aplicación
const socketSyncInstance = new SocketSyncService();
export default socketSyncInstance;